import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  getDoc,
  getDocs,
  where
} from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import * as XLSX from 'xlsx';

// Debug component to check auth status
const AuthDebugInfo = () => {
  const { currentUser } = useAuth();
  const [debugInfo, setDebugInfo] = useState({
    loading: true,
    firebaseUser: null,
    authContextUser: null,
    userDocData: null,
    writeTestResult: null,
    errors: []
  });

  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const errors = [];
      
      try {
        // Get Firebase Auth current user
        const firebaseUser = auth.currentUser;
        
        // Check if we have a user document
        let userDocData = null;
        if (firebaseUser) {
          try {
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              userDocData = userDocSnap.data();
            } else {
              errors.push('User document does not exist in Firestore');
            }
          } catch (error) {
            errors.push(`Error fetching user document: ${error.message}`);
          }
        } else {
          errors.push('No Firebase Auth user found');
        }

        // Test write operation to catalogueProducts
        let writeTestResult = null;
        if (firebaseUser) {
          try {
            // Try to create a test document (we'll delete it immediately)
            const testDoc = {
              name: 'DEBUG_TEST_PRODUCT',
              category: 'test',
              status: 'draft',
              price: 0,
              createdAt: serverTimestamp()
            };
            
            const docRef = await addDoc(collection(db, 'catalogueProducts'), testDoc);
            
            // Immediately delete the test document
            await deleteDoc(doc(db, 'catalogueProducts', docRef.id));
            
            writeTestResult = 'SUCCESS - Write test passed';
          } catch (error) {
            writeTestResult = `FAILED - Write test error: ${error.message}`;
            errors.push(`Write test failed: ${error.message}`);
          }
        } else {
          writeTestResult = 'SKIPPED - No authenticated user';
        }

        setDebugInfo({
          loading: false,
          firebaseUser: firebaseUser ? {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified
          } : null,
          authContextUser: currentUser ? {
            uid: currentUser.uid,
            email: currentUser.email,
            isAdmin: currentUser.isAdmin
          } : null,
          userDocData,
          writeTestResult,
          errors
        });
      } catch (error) {
        errors.push(`General error: ${error.message}`);
        setDebugInfo(prev => ({
          ...prev,
          loading: false,
          errors
        }));
      }
    };

    checkAuthStatus();
  }, [currentUser]);

  if (!showDebug) {
    return (
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <button
          onClick={() => setShowDebug(true)}
          style={{
            background: '#ff6b35',
            color: 'white',
            border: 'none',
            padding: '10px 15px',
            fontSize: '12px',
            cursor: 'pointer',
            borderRadius: '4px'
          }}
        >
          Show Auth Debug
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      width: '400px',
      maxHeight: '80vh',
      overflow: 'auto',
      background: 'white',
      border: '3px solid #ff6b35',
      padding: '20px',
      fontSize: '12px',
      zIndex: 1000,
      fontFamily: 'monospace'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h3 style={{ margin: 0, color: '#ff6b35' }}>AUTH DEBUG INFO</h3>
        <button
          onClick={() => setShowDebug(false)}
          style={{
            background: '#ccc',
            border: 'none',
            padding: '5px 10px',
            cursor: 'pointer'
          }}
        >
          ✕
        </button>
      </div>

      {debugInfo.loading ? (
        <div>Loading debug info...</div>
      ) : (
        <div>
          <div style={{ marginBottom: '15px' }}>
            <strong>Firebase Auth User:</strong>
            <pre style={{ background: '#f5f5f5', padding: '10px', margin: '5px 0' }}>
              {JSON.stringify(debugInfo.firebaseUser, null, 2)}
            </pre>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <strong>Auth Context User:</strong>
            <pre style={{ background: '#f5f5f5', padding: '10px', margin: '5px 0' }}>
              {JSON.stringify(debugInfo.authContextUser, null, 2)}
            </pre>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <strong>User Document Data:</strong>
            <pre style={{ background: '#f5f5f5', padding: '10px', margin: '5px 0' }}>
              {JSON.stringify(debugInfo.userDocData, null, 2)}
            </pre>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <strong>Admin Status Check:</strong>
            <div style={{ 
              padding: '10px', 
              background: debugInfo.authContextUser?.isAdmin ? '#d4edda' : '#f8d7da',
              color: debugInfo.authContextUser?.isAdmin ? '#155724' : '#721c24',
              margin: '5px 0'
            }}>
              {debugInfo.authContextUser?.isAdmin ? 'USER IS ADMIN ✓' : 'USER IS NOT ADMIN ✗'}
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <strong>Collection Access Test:</strong>
            <div style={{ 
              padding: '10px', 
              background: debugInfo.writeTestResult?.includes('SUCCESS') ? '#d4edda' : '#f8d7da',
              color: debugInfo.writeTestResult?.includes('SUCCESS') ? '#155724' : '#721c24',
              margin: '5px 0'
            }}>
              catalogueProducts: {debugInfo.writeTestResult}
            </div>
          </div>

          {debugInfo.errors.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ color: '#dc3545' }}>Errors:</strong>
              {debugInfo.errors.map((error, index) => (
                <div key={index} style={{ 
                  background: '#f8d7da', 
                  color: '#721c24', 
                  padding: '5px', 
                  margin: '2px 0',
                  fontSize: '11px'
                }}>
                  {error}
                </div>
              ))}
            </div>
          )}

          <div style={{ fontSize: '10px', color: '#666', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
            <strong>Expected for successful add:</strong><br/>
            • User must be logged in<br/>
            • User document must exist with isAdmin: true<br/>
            • Firebase rules must allow write to catalogueProducts<br/>
            • Write test should succeed
          </div>
        </div>
      )}
    </div>
  );
};

const CatalogueManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    origin: '',
    producer: '',
    region: '',
    process: '',
    altitude: '',
    varietal: '',
    notes: '',
    story: '',
    price: '',
    category: 'beans',
    status: 'active',
    featured: false,
    bestSeller: false,
    seasonal: false,
    image: '',
    producerImage: '',
    // New fields for weight and type variations
    weightOptions: [
      { weight: '250', unit: 'g', price: '' }
    ],
    typeOptions: [
      { type: 'filter', price: '' }
    ],
    inventory: {
      totalStock: 0,
      lowStockThreshold: 10,
      trackInventory: true
    }
  });

  // Real-time products listener
  useEffect(() => {
    const q = query(collection(db, 'catalogueProducts'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching products:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('inventory.')) {
      const inventoryField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        inventory: {
          ...prev.inventory,
          [inventoryField]: type === 'number' ? parseInt(value) || 0 : value
        }
      }));
    } else if (name.startsWith('weightOptions.')) {
      const [_, index, field] = name.split('.');
      const indexNum = parseInt(index);
      setFormData(prev => ({
        ...prev,
        weightOptions: prev.weightOptions.map((option, i) => 
          i === indexNum ? { ...option, [field]: value } : option
        )
      }));
    } else if (name.startsWith('typeOptions.')) {
      const [_, index, field] = name.split('.');
      const indexNum = parseInt(index);
      setFormData(prev => ({
        ...prev,
        typeOptions: prev.typeOptions.map((option, i) => 
          i === indexNum ? { ...option, [field]: value } : option
        )
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || '' : value
      }));
    }
  };

  // Add new weight option
  const addWeightOption = () => {
    setFormData(prev => ({
      ...prev,
      weightOptions: [...prev.weightOptions, { weight: '', unit: 'g', price: '' }]
    }));
  };

  // Remove weight option
  const removeWeightOption = (index) => {
    setFormData(prev => ({
      ...prev,
      weightOptions: prev.weightOptions.filter((_, i) => i !== index)
    }));
  };

  // Add new type option
  const addTypeOption = () => {
    setFormData(prev => ({
      ...prev,
      typeOptions: [...prev.typeOptions, { type: '', price: '' }]
    }));
  };

  // Remove type option
  const removeTypeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      typeOptions: prev.typeOptions.filter((_, i) => i !== index)
    }));
  };

  // Add new product
  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'catalogueProducts'), productData);
      
      // Reset form
      setFormData({
        name: '',
        origin: '',
        producer: '',
        region: '',
        process: '',
        altitude: '',
        varietal: '',
        notes: '',
        story: '',
        price: '',
        category: 'beans',
        status: 'active',
        featured: false,
        bestSeller: false,
        seasonal: false,
        image: '',
        producerImage: '',
        weightOptions: [
          { weight: '250', unit: 'g', price: '' }
        ],
        typeOptions: [
          { type: 'filter', price: '' }
        ],
        inventory: {
          totalStock: 0,
          lowStockThreshold: 10,
          trackInventory: true
        }
      });
      setShowForm(false);
      
      alert('Product added successfully!');
    } catch (error) {
      console.error('Error adding product:', error);
      
      // Enhanced error message with more details
      let errorMessage = 'ADD PRODUCT FAILED: ';
      
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check if you are logged in as an admin. Current user admin status can be viewed in the debug panel.';
      } else if (error.code === 'unauthenticated') {
        errorMessage += 'Not authenticated. Please log in and try again.';
      } else if (error.message.includes('Missing or insufficient permissions')) {
        errorMessage += 'Insufficient permissions. You need admin access to add products. Check the debug panel for authentication details.';
      } else {
        errorMessage += `${error.message} (Code: ${error.code || 'unknown'})`;
      }
      
      alert(errorMessage);
    }
  };

  // Edit product
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      origin: product.origin || '',
      producer: product.producer || '',
      region: product.region || '',
      process: product.process || '',
      altitude: product.altitude || '',
      varietal: product.varietal || '',
      notes: product.notes || '',
      story: product.story || '',
      price: product.price || '',
      category: product.category || 'beans',
      status: product.status || 'active',
      featured: product.featured || false,
      bestSeller: product.bestSeller || false,
      seasonal: product.seasonal || false,
      image: product.image || '',
      producerImage: product.producerImage || '',
      weightOptions: product.weightOptions || [
        { weight: '250', unit: 'g', price: '' }
      ],
      typeOptions: product.typeOptions || [
        { type: 'filter', price: '' }
      ],
      inventory: {
        totalStock: product.inventory?.totalStock || 0,
        lowStockThreshold: product.inventory?.lowStockThreshold || 10,
        trackInventory: product.inventory?.trackInventory ?? true
      }
    });
    setShowForm(true);
  };

  // Update product
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, 'catalogueProducts', editingProduct.id), productData);
      
      setEditingProduct(null);
      setShowForm(false);
      setFormData({
        name: '',
        origin: '',
        producer: '',
        region: '',
        process: '',
        altitude: '',
        varietal: '',
        notes: '',
        story: '',
        price: '',
        category: 'beans',
        status: 'active',
        featured: false,
        bestSeller: false,
        seasonal: false,
        image: '',
        producerImage: '',
        weightOptions: [
          { weight: '250', unit: 'g', price: '' }
        ],
        typeOptions: [
          { type: 'filter', price: '' }
        ],
        inventory: {
          totalStock: 0,
          lowStockThreshold: 10,
          trackInventory: true
        }
      });
      
      alert('Product updated successfully!');
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error updating product. Please try again.');
    }
  };

  // Delete product
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'catalogueProducts', productId));
      alert('Product deleted successfully!');
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product. Please try again.');
    }
  };

  // Toggle product status
  const toggleProductStatus = async (productId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    try {
      await updateDoc(doc(db, 'catalogueProducts', productId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error toggling product status:', error);
      alert('Error updating product status.');
    }
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.producer?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'All' || product.category === filterCategory;
    const matchesStatus = filterStatus === 'All' || product.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Get unique categories
  const categories = ['All', ...new Set(products.map(p => p.category).filter(Boolean))];

  // Excel Download Function
  const downloadExcel = () => {
    const excelData = products.map(product => ({
      'Product Name': product.name || '',
      'Category': product.category || '',
      'Origin': product.origin || '',
      'Producer': product.producer || '',
      'Region': product.region || '',
      'Process': product.process || '',
      'Altitude': product.altitude || '',
      'Varietal': product.varietal || '',
      'Notes': product.notes || '',
      'Story': product.story || '',
      'Price': product.price || '',
      'Status': product.status || '',
      'Featured': product.featured ? 'Yes' : 'No',
      'Best Seller': product.bestSeller ? 'Yes' : 'No',
      'Seasonal': product.seasonal ? 'Yes' : 'No',
      'Image URL': product.image || '',
      'Producer Image URL': product.producerImage || '',
      'Total Stock': product.inventory?.totalStock || 0,
      'Low Stock Threshold': product.inventory?.lowStockThreshold || 10,
      'Track Inventory': product.inventory?.trackInventory ? 'Yes' : 'No',
      'Weight Options': product.weightOptions ? JSON.stringify(product.weightOptions) : '',
      'Type Options': product.typeOptions ? JSON.stringify(product.typeOptions) : ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
    
    // Auto-adjust column widths
    const maxWidth = excelData.length ? Object.keys(excelData[0]).reduce((w, name) => ({
      ...w,
      [name]: Math.max(w[name] || 10, name.length, ...excelData.map(row => String(row[name] || '').length))
    }), {}) : {};
    
    worksheet['!cols'] = Object.keys(maxWidth).map(name => ({ width: Math.min(maxWidth[name], 50) }));
    
    XLSX.writeFile(workbook, `catalogue-products-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Excel Upload Function
  const handleExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        console.log('Excel data:', jsonData);

        let successCount = 0;
        let errorCount = 0;

        for (const row of jsonData) {
          try {
            const productName = row['Product Name'] || '';
            if (!productName) {
              console.log('Skipping row with empty product name');
              continue;
            }

            // Check if product with same name already exists
            const existingProductQuery = query(
              collection(db, 'catalogueProducts'),
              where('name', '==', productName)
            );
            const existingProductSnapshot = await getDocs(existingProductQuery);

            const productData = {
              name: productName,
              category: row['Category'] || 'beans',
              origin: row['Origin'] || '',
              producer: row['Producer'] || '',
              region: row['Region'] || '',
              process: row['Process'] || '',
              altitude: row['Altitude'] || '',
              varietal: row['Varietal'] || '',
              notes: row['Notes'] || '',
              story: row['Story'] || '',
              price: parseFloat(row['Price']) || 0,
              status: row['Status'] || 'active',
              featured: row['Featured'] === 'Yes',
              bestSeller: row['Best Seller'] === 'Yes',
              seasonal: row['Seasonal'] === 'Yes',
              image: row['Image URL'] || '',
              producerImage: row['Producer Image URL'] || '',
              inventory: {
                totalStock: parseInt(row['Total Stock']) || 0,
                lowStockThreshold: parseInt(row['Low Stock Threshold']) || 10,
                trackInventory: row['Track Inventory'] === 'Yes'
              },
              weightOptions: row['Weight Options'] ? JSON.parse(row['Weight Options']) : [
                { weight: '250', unit: 'g', price: '' }
              ],
              typeOptions: row['Type Options'] ? JSON.parse(row['Type Options']) : [
                { type: 'filter', price: '' }
              ],
              updatedAt: serverTimestamp()
            };

            if (!existingProductSnapshot.empty) {
              // Update existing product
              const docId = existingProductSnapshot.docs[0].id;
              await updateDoc(doc(db, 'catalogueProducts', docId), productData);
              console.log(`Updated existing product: ${productName}`);
            } else {
              // Add new product
              productData.createdAt = serverTimestamp();
              await addDoc(collection(db, 'catalogueProducts'), productData);
              console.log(`Added new product: ${productName}`);
            }
            
            successCount++;
          } catch (error) {
            console.error('Error processing product:', error);
            errorCount++;
          }
        }

        alert(`Upload completed!\nSuccess: ${successCount} products\nErrors: ${errorCount} products`);
        
        // Reset file input
        event.target.value = '';
        
      } catch (error) {
        console.error('Error processing Excel file:', error);
        alert('Error processing Excel file. Please check the format and try again.');
      }
    };

    reader.readAsBinaryString(file);
  };

  // Download Template Function
  const downloadTemplate = () => {
    const templateData = [{
      'Product Name': 'Example Coffee',
      'Category': 'beans',
      'Origin': 'Brazil',
      'Producer': 'Example Farm',
      'Region': 'Minas Gerais',
      'Process': 'Natural',
      'Altitude': '1200 MASL',
      'Varietal': 'Arabica',
      'Notes': 'Chocolate, Nutty, Sweet',
      'Story': 'A story about the producer and coffee origin...',
      'Price': '50',
      'Status': 'active',
      'Featured': 'No',
      'Best Seller': 'No',
      'Seasonal': 'No',
      'Image URL': 'https://example.com/image.jpg',
      'Producer Image URL': 'https://example.com/producer.jpg',
      'Total Stock': '100',
      'Low Stock Threshold': '10',
      'Track Inventory': 'Yes',
      'Weight Options': '[{"weight":"250","unit":"g","price":"50"},{"weight":"1","unit":"kg","price":"180"}]',
      'Type Options': '[{"type":"filter","price":"50"},{"type":"espresso","price":"55"}]'
    }];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    
    XLSX.writeFile(workbook, 'catalogue-products-template.xlsx');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading catalogue products...</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Debug Component */}
      <AuthDebugInfo />
      
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '40px',
        borderBottom: '2px solid #000',
        paddingBottom: '20px'
      }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '100',
          letterSpacing: '4px',
          textTransform: 'uppercase',
          margin: 0
        }}>
          Catalogue Management
        </h1>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* Excel Actions */}
          <div style={{ display: 'flex', gap: '5px' }}>
            <button
              onClick={downloadTemplate}
              style={{
                background: 'transparent',
                color: '#000',
                border: '2px solid #000',
                padding: '10px 15px',
                fontSize: '10px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }}
            >
              Download Template
            </button>
            
            <button
              onClick={downloadExcel}
              style={{
                background: 'transparent',
                color: '#000',
                border: '2px solid #000',
                padding: '10px 15px',
                fontSize: '10px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }}
            >
              Export Excel
            </button>
            
            <div style={{ position: 'relative' }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer'
                }}
              />
              <button
                style={{
                  background: 'transparent',
                  color: '#000',
                  border: '2px solid #000',
                  padding: '10px 15px',
                  fontSize: '10px',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer'
                }}
              >
                Import Excel
              </button>
            </div>
          </div>
          
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingProduct(null);
              setFormData({
                name: '',
                origin: '',
                producer: '',
                region: '',
                process: '',
                altitude: '',
                varietal: '',
                notes: '',
                story: '',
                price: '',
                category: 'beans',
                status: 'active',
                featured: false,
                bestSeller: false,
                seasonal: false,
                image: '',
                producerImage: '',
                weightOptions: [
                  { weight: '250', unit: 'g', price: '' }
                ],
                typeOptions: [
                  { type: 'filter', price: '' }
                ],
                inventory: {
                  totalStock: 0,
                  lowStockThreshold: 10,
                  trackInventory: true
                }
              });
            }}
            style={{
              background: '#000',
              color: 'white',
              border: '2px solid #000',
              padding: '15px 30px',
              fontSize: '12px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer'
            }}
          >
            {showForm ? 'Cancel' : 'Add Product'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '40px'
      }}>
        <div style={{
          border: '2px solid #000',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '600' }}>{products.length}</div>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Total Products
          </div>
        </div>
        <div style={{
          border: '2px solid #000',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '600' }}>
            {products.filter(p => p.status === 'active').length}
          </div>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Active Products
          </div>
        </div>
        <div style={{
          border: '2px solid #000',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '600' }}>
            {products.filter(p => p.featured).length}
          </div>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Featured Products
          </div>
        </div>
        <div style={{
          border: '2px solid #000',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '600' }}>
            {products.filter(p => p.inventory?.totalStock <= p.inventory?.lowStockThreshold).length}
          </div>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Low Stock Alerts
          </div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{
          border: '2px solid #000',
          padding: '40px',
          marginBottom: '40px',
          background: '#f9f9f9'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '300',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            marginBottom: '30px'
          }}>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h2>
          
          <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '30px',
              marginBottom: '30px'
            }}>
              {/* Basic Info */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Product Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Price (QAR) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Origin
                </label>
                <input
                  type="text"
                  name="origin"
                  value={formData.origin}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Producer
                </label>
                <input
                  type="text"
                  name="producer"
                  value={formData.producer}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Region
                </label>
                <input
                  type="text"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Process
                </label>
                <input
                  type="text"
                  name="process"
                  value={formData.process}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Altitude
                </label>
                <input
                  type="text"
                  name="altitude"
                  value={formData.altitude}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Varietal
                </label>
                <input
                  type="text"
                  name="varietal"
                  value={formData.varietal}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="beans">Beans</option>
                  <option value="drip-bags">Drip Bags</option>
                  <option value="equipment">Equipment</option>
                  <option value="merchandise">Merchandise</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            {/* Tasting Notes */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                Tasting Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #000',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Producer Story */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                Producer Story
              </label>
              <textarea
                name="story"
                value={formData.story}
                onChange={handleInputChange}
                rows="4"
                placeholder="Tell the story about the producer, farm, or coffee origin..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #000',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Images */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '30px',
              marginBottom: '30px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Product Image URL
                </label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Producer Image URL
                </label>
                <input
                  type="url"
                  name="producerImage"
                  value={formData.producerImage}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Inventory */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '30px',
              marginBottom: '30px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Total Stock
                </label>
                <input
                  type="number"
                  name="inventory.totalStock"
                  value={formData.inventory.totalStock}
                  onChange={handleInputChange}
                  min="0"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Low Stock Threshold
                </label>
                <input
                  type="number"
                  name="inventory.lowStockThreshold"
                  value={formData.inventory.lowStockThreshold}
                  onChange={handleInputChange}
                  min="0"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #000',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Weight Options */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <label style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Weight Options & Pricing
                </label>
                <button
                  type="button"
                  onClick={addWeightOption}
                  style={{
                    background: 'transparent',
                    border: '1px solid #000',
                    padding: '5px 10px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    textTransform: 'uppercase'
                  }}
                >
                  Add Weight
                </button>
              </div>
              
              {formData.weightOptions.map((option, index) => (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 1fr auto',
                  gap: '10px',
                  marginBottom: '10px',
                  alignItems: 'end'
                }}>
                  <div>
                    <input
                      type="text"
                      name={`weightOptions.${index}.weight`}
                      value={option.weight}
                      onChange={handleInputChange}
                      placeholder="250"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #000',
                        fontSize: '12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <select
                      name={`weightOptions.${index}.unit`}
                      value={option.unit}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #000',
                        fontSize: '12px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="oz">oz</option>
                      <option value="lb">lb</option>
                    </select>
                  </div>
                  <div>
                    <input
                      type="number"
                      name={`weightOptions.${index}.price`}
                      value={option.price}
                      onChange={handleInputChange}
                      placeholder="Price (QAR)"
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #000',
                        fontSize: '12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  {formData.weightOptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeWeightOption(index)}
                      style={{
                        background: '#ff0000',
                        color: 'white',
                        border: '1px solid #ff0000',
                        padding: '8px 10px',
                        fontSize: '10px',
                        cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Type Options */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <label style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>
                  Type Options & Pricing
                </label>
                <button
                  type="button"
                  onClick={addTypeOption}
                  style={{
                    background: 'transparent',
                    border: '1px solid #000',
                    padding: '5px 10px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    textTransform: 'uppercase'
                  }}
                >
                  Add Type
                </button>
              </div>
              
              {formData.typeOptions.map((option, index) => (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: '10px',
                  marginBottom: '10px',
                  alignItems: 'end'
                }}>
                  <div>
                    <input
                      type="text"
                      name={`typeOptions.${index}.type`}
                      value={option.type}
                      onChange={handleInputChange}
                      placeholder="e.g., filter, espresso, drip"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #000',
                        fontSize: '12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      name={`typeOptions.${index}.price`}
                      value={option.price}
                      onChange={handleInputChange}
                      placeholder="Price (QAR)"
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #000',
                        fontSize: '12px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  {formData.typeOptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTypeOption(index)}
                      style={{
                        background: '#ff0000',
                        color: 'white',
                        border: '1px solid #ff0000',
                        padding: '8px 10px',
                        fontSize: '10px',
                        cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Checkboxes */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Featured
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                <input
                  type="checkbox"
                  name="bestSeller"
                  checked={formData.bestSeller}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Best Seller
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                <input
                  type="checkbox"
                  name="seasonal"
                  checked={formData.seasonal}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Seasonal
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                <input
                  type="checkbox"
                  name="inventory.trackInventory"
                  checked={formData.inventory.trackInventory}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Track Inventory
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              style={{
                background: '#000',
                color: 'white',
                border: '2px solid #000',
                padding: '15px 40px',
                fontSize: '12px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }}
            >
              {editingProduct ? 'Update Product' : 'Add Product'}
            </button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '30px',
        padding: '20px',
        border: '2px solid #000'
      }}>
        <div>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '12px',
            fontWeight: '600',
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}>
            Search
          </label>
          <input
            type="text"
            placeholder="TYPE TO SEARCH..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #000',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '12px',
            fontWeight: '600',
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}>
            Category
          </label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #000',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '12px',
            fontWeight: '600',
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}>
            Status
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #000',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          >
            <option value="All">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Results Counter */}
      <div style={{
        marginBottom: '20px',
        fontSize: '12px',
        letterSpacing: '1px',
        textTransform: 'uppercase'
      }}>
        Showing {filteredProducts.length} of {products.length} products
      </div>

      {/* Products Table */}
      <div style={{ border: '2px solid #000' }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '60px 180px 80px 100px 120px 120px 80px 80px 80px 150px',
          background: '#f0f0f0',
          borderBottom: '2px solid #000',
          fontSize: '10px',
          fontWeight: '600',
          letterSpacing: '1px',
          textTransform: 'uppercase'
        }}>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Image</div>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Name</div>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Category</div>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Origin</div>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Weights</div>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Types</div>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Stock</div>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Status</div>
          <div style={{ padding: '15px 10px', borderRight: '1px solid #000' }}>Flags</div>
          <div style={{ padding: '15px 10px' }}>Actions</div>
        </div>

        {/* Table Body */}
        {filteredProducts.length === 0 ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
            fontSize: '14px',
            color: '#666'
          }}>
            No products found
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div
              key={product.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 180px 80px 100px 120px 120px 80px 80px 80px 150px',
                borderBottom: '1px solid #ccc',
                fontSize: '12px',
                alignItems: 'center'
              }}
            >
              <div style={{ padding: '10px', borderRight: '1px solid #ccc' }}>
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    style={{
                      width: '40px',
                      height: '40px',
                      objectFit: 'cover',
                      border: '1px solid #ccc'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#f0f0f0',
                    border: '1px solid #ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px'
                  }}>
                    No Image
                  </div>
                )}
              </div>
              
              <div style={{ padding: '10px', borderRight: '1px solid #ccc' }}>
                <div style={{ fontWeight: '600' }}>{product.name}</div>
                <div style={{ fontSize: '10px', color: '#666' }}>{product.producer}</div>
              </div>
              
              <div style={{ padding: '10px', borderRight: '1px solid #ccc', textTransform: 'uppercase' }}>
                {product.category}
              </div>
              
              <div style={{ padding: '10px', borderRight: '1px solid #ccc' }}>
                <div>{product.origin}</div>
                <div style={{ fontSize: '10px', color: '#666' }}>{product.region}</div>
              </div>
              
              <div style={{ padding: '10px', borderRight: '1px solid #ccc', fontSize: '10px' }}>
                {product.weightOptions && product.weightOptions.length > 0 ? (
                  product.weightOptions.map((option, idx) => (
                    <div key={idx}>
                      {option.weight}{option.unit}: QAR {option.price}
                    </div>
                  ))
                ) : (
                  <div>QAR {product.price || 'N/A'}</div>
                )}
              </div>
              
              <div style={{ padding: '10px', borderRight: '1px solid #ccc', fontSize: '10px' }}>
                {product.typeOptions && product.typeOptions.length > 0 ? (
                  product.typeOptions.map((option, idx) => (
                    <div key={idx}>
                      {option.type}: QAR {option.price}
                    </div>
                  ))
                ) : (
                  <div>Standard</div>
                )}
              </div>
              
              <div style={{ padding: '10px', borderRight: '1px solid #ccc' }}>
                <div style={{
                  color: product.inventory?.totalStock <= product.inventory?.lowStockThreshold ? '#ff0000' : '#000'
                }}>
                  {product.inventory?.totalStock || 0}
                </div>
                {product.inventory?.totalStock <= product.inventory?.lowStockThreshold && (
                  <div style={{ fontSize: '10px', color: '#ff0000' }}>LOW</div>
                )}
              </div>
              
              <div style={{ padding: '10px', borderRight: '1px solid #ccc' }}>
                <span style={{
                  padding: '4px 8px',
                  fontSize: '10px',
                  background: product.status === 'active' ? '#00ff00' : product.status === 'draft' ? '#ffff00' : '#ff0000',
                  color: product.status === 'active' ? '#000' : '#000',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {product.status}
                </span>
              </div>
              
              <div style={{ padding: '10px', borderRight: '1px solid #ccc', fontSize: '10px' }}>
                {product.featured && <div>FEATURED</div>}
                {product.bestSeller && <div>BESTSELLER</div>}
                {product.seasonal && <div>SEASONAL</div>}
              </div>
              
              <div style={{ padding: '10px', display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => handleEditProduct(product)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #000',
                    padding: '5px 8px',
                    fontSize: '10px',
                    cursor: 'pointer'
                  }}
                >
                  EDIT
                </button>
                <button
                  onClick={() => toggleProductStatus(product.id, product.status)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #000',
                    padding: '5px 8px',
                    fontSize: '10px',
                    cursor: 'pointer'
                  }}
                >
                  {product.status === 'active' ? 'HIDE' : 'SHOW'}
                </button>
                <button
                  onClick={() => handleDeleteProduct(product.id)}
                  style={{
                    background: '#ff0000',
                    color: 'white',
                    border: '1px solid #ff0000',
                    padding: '5px 8px',
                    fontSize: '10px',
                    cursor: 'pointer'
                  }}
                >
                  DEL
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CatalogueManagement;